import { LogoWrapper } from "@Components/common/account/styled-components";
import { AccountPageLayout, CharacterInputGrid, ProfileCard, ProfileGridContainer, ProfileSearch } from "@Components/game/account";
import AddProfileCard from "@Components/game/account/add-profile-card";
import { BackButton, HeaderButton } from "@Components/game/header/styled-components";
import CenteredLoadingOverlay from "@Components/generic/centered-loading-overlay";
import HapticsButtonWrapper from "@Components/generic/haptics-button-wrapper";
import Icon from "@Components/generic/icon";
import { ProfileSelectionProvider, useProfileSelection } from "@Context/common/account/profile-selection-context";
import { Events } from "@Context/common/general/context";
import Permissions from "@Context/common/permissions/permissions";
import AccountRoles from "@Enums/AccountRoles";
import { faArrowLeft, faPlusCircle } from "@fortawesome/pro-solid-svg-icons";
import useI18n from "@i18n";
import { Box, capitalize, Stack, useTheme } from "@mui/material";
import LogoSmall from "@Resources/logo_small.svg";
import { useEffect } from "react";

const ProfileSelectionContent = () => {
  const { searchTerm, filteredProfiles, manyProfiles, setSearchTerm, handleProfileSelect, handleClearSearch, handleAddProfile, handleInviteUser, isLoadingAccounts } =
    useProfileSelection();
  const theme = useTheme();
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
  const { i18n } = useI18n();

  useEffect(() => {
    // Events.app.navBarHidden.emit({ hidden: false });
    Events.app.setHeaderAltSections.emit({
      leftSection: (
        <BackButton onClick={()=>{
          Events.app.navigateBack.emit();
        }}>
          <Icon icon={faArrowLeft} color={theme.palette.dsBase.v200} fontSize={14} sx={{ width: theme.spacing(1.75) }} />
        </BackButton>
      ),
      centerSection: <></>,
      rightSection: (
        <Permissions editor={AccountRoles.ADMIN} viewer={AccountRoles.ADMIN}>
          <HapticsButtonWrapper>
            <HeaderButton onClick={handleInviteUser}>
              <Icon icon={faPlusCircle} color={theme.palette.dsBase.v200} fontSize={24} />
            </HeaderButton>
          </HapticsButtonWrapper>
        </Permissions>
      ),
    });
    return () => {
      Events.app.setHeaderAltSections.emit(null);
      Events.app.navBarHidden.emit({ hidden: false });
    };
  }, []);

  const handleLetterClick = (letter: string) => {
    const newSearchTerm = searchTerm + letter;
    setSearchTerm(newSearchTerm);
  };

  const handleBackspace = () => {
    const newSearchTerm = searchTerm.slice(0, -1);
    setSearchTerm(newSearchTerm);
  };

  return (
    <AccountPageLayout
      pageId="SELECT_PROFILE"
      title={i18n("account.profile.selection.who")}
      description={manyProfiles ? i18n("account.profile.selection.search.instruction") : undefined}
      centered={!manyProfiles}
    >
      {isLoadingAccounts && <CenteredLoadingOverlay  loading={isLoadingAccounts}/>}
      {!isLoadingAccounts && manyProfiles && (
        <Stack spacing={6}>
          <CharacterInputGrid characters={alphabet} onCharacterClick={handleLetterClick} onBackspace={handleBackspace} />
          <ProfileSearch searchTerm={searchTerm} onSearchChange={(value) => setSearchTerm(value)} onClear={handleClearSearch} />
        </Stack>
      )}

      <ProfileGridContainer sx={!manyProfiles ? { gridTemplateColumns: "repeat(6, 1fr)" } : undefined}>
        {!manyProfiles && <Box />}
        <Permissions editor={AccountRoles.ADMIN} viewer={AccountRoles.ADMIN}>
          <AddProfileCard onClick={handleInviteUser} />
        </Permissions>
        {filteredProfiles.map((profile) => (
          <ProfileCard
            key={profile.doc_id}
            name={capitalize(profile.first_name) + " " + capitalize(profile.last_name)}
            league={profile.rating?.toString()}
            avatarUrl={profile.avatar || undefined}
            onClick={() => handleProfileSelect(profile.doc_id)}
          />
        ))}
      </ProfileGridContainer>
    </AccountPageLayout>
  );
};

const ProfileSelection = () => {
  return (
    <ProfileSelectionProvider>
      <ProfileSelectionContent />
    </ProfileSelectionProvider>
  );
};

export default ProfileSelection;
